import React, { useState, useEffect } from 'react';
import { ExternalLink, PlayCircle } from 'lucide-react';
import api from '../services/api';

const TutorialesPage = () => {
  const [tutorials, setTutorials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('Todos');

  useEffect(() => {
    loadTutorials();
  }, []);

  const loadTutorials = async () => {
    try {
      const response = await api.get('/api/tutorials/public');
      setTutorials(response.data.tutorials || []);
    } catch (error) {
      console.error('Error cargando tutoriales:', error);
    } finally {
      setLoading(false);
    }
  };

  // Extraer ID de video de YouTube desde URL
  const getYouTubeId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  // Obtener thumbnail de YouTube
  const getYouTubeThumbnail = (url) => {
    const videoId = getYouTubeId(url);
    return videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : null;
  };

  // Obtener categorías únicas
  const categories = ['Todos', ...new Set(tutorials.map(t => t.category))];

  // Filtrar por categoría
  const filteredTutorials = selectedCategory === 'Todos' 
    ? tutorials 
    : tutorials.filter(t => t.category === selectedCategory);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            📺 Videotutoriales
          </h1>
          <p className="text-gray-400 text-lg">
            Aprende a usar todas las funcionalidades del sistema
          </p>
        </div>

        {/* Filtros por categoría */}
        <div className="flex flex-wrap justify-center gap-3 mb-8">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-6 py-2 rounded-full font-semibold transition-all ${
                selectedCategory === cat
                  ? 'bg-blue-600 text-white shadow-lg scale-105'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Videos Grid */}
        {filteredTutorials.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-400 text-lg">
              No hay videotutoriales disponibles en esta categoría
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTutorials.map((tutorial) => {
              const thumbnail = getYouTubeThumbnail(tutorial.youtube_url);
              return (
                <a
                  key={tutorial.id}
                  href={tutorial.youtube_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-gray-800 rounded-xl overflow-hidden hover:scale-105 transition-all shadow-lg hover:shadow-2xl group"
                >
                  {/* Thumbnail */}
                  <div className="relative aspect-video bg-gray-700">
                    {thumbnail ? (
                      <img
                        src={thumbnail}
                        alt={tutorial.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = `https://img.youtube.com/vi/${getYouTubeId(tutorial.youtube_url)}/hqdefault.jpg`;
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <PlayCircle className="w-16 h-16 text-gray-600" />
                      </div>
                    )}
                    
                    {/* Play overlay */}
                    <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-all flex items-center justify-center">
                      <div className="bg-red-600 rounded-full p-4 group-hover:scale-110 transition-transform">
                        <PlayCircle className="w-12 h-12 text-white" fill="white" />
                      </div>
                    </div>
                    
                    {/* Categoría badge */}
                    <div className="absolute top-3 left-3">
                      <span className="bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                        {tutorial.category}
                      </span>
                    </div>
                  </div>

                  {/* Info */}
                  <div className="p-4">
                    <h3 className="font-bold text-lg mb-2 group-hover:text-blue-400 transition-colors">
                      {tutorial.title}
                    </h3>
                    {tutorial.description && (
                      <p className="text-gray-400 text-sm line-clamp-2">
                        {tutorial.description}
                      </p>
                    )}
                    
                    {/* Link icon */}
                    <div className="flex items-center gap-2 mt-3 text-blue-400 text-sm">
                      <ExternalLink className="w-4 h-4" />
                      <span>Ver en YouTube</span>
                    </div>
                  </div>
                </a>
              );
            })}
          </div>
        )}

        {/* Footer */}
        <div className="text-center mt-12 text-gray-500 text-sm">
          <p>¿No encuentras lo que buscas? Contáctanos para sugerir nuevos tutoriales</p>
        </div>
      </div>
    </div>
  );
};

export default TutorialesPage;
